import { GitDiff } from '../../models/git-worker.model';

// --- a/original-path.ext
// --- /dev/null
const DIFF_HEADER_FROM_PATH_RE = /^--- a?\/?(.+)/;
// +++ b/new-path.ext
// +++ /dev/null
const DIFF_HEADER_TO_PATH_RE = /^\+\+\+ b?\/?(.+)/;

// index 006d26b..9b679a1 100644
const DIFF_INDEX_HASHES_RE = /^index (.{7})..(.{7})/;

const DIFF_STARTING_LINE = 'diff --git';

const DELETED_FILE_PATH = 'dev/null';

export class DiffParser {
  private text = '';

  private start = 0;
  private end = -1;

  parse(text: string): GitDiff[] {
    this.text = text;

    const diffs: GitDiff[] = [];

    try {
      while (this.nextLine()) {
        const diff = this.parseDiff();
        if (diff) {
          diffs.push(diff);
        }
      }

      return diffs;
    } finally {
      this.reset();
    }
  }

  private parseDiff(): GitDiff | undefined {
    // git git-diff header
    // git-diff --git a/path-to-file.ext
    if (!this.lineStartsWith(DIFF_STARTING_LINE)) {
      return;
    }

    const diff = {} as GitDiff;

    let from = '';
    let to = '';
    // parse git-diff header
    while (this.nextLine()) {
      if (this.lineStartsWith('index ')) {
        const hashes = DIFF_INDEX_HASHES_RE.exec(this.currentLine)!;
        diff.oldHash = hashes[1] ?? '';
        diff.newHash = hashes[2] ?? '';
      }
      if (this.lineStartsWith('---')) {
        from = DIFF_HEADER_FROM_PATH_RE.exec(this.currentLine)?.[1] ?? '';
      }
      if (this.lineStartsWith('+++')) {
        to = DIFF_HEADER_TO_PATH_RE.exec(this.currentLine)?.[1] ?? '';
        break;
      }
    }

    if (!to) {
      return;
    }

    diff.path = to;
    let status: string;
    if (to !== DELETED_FILE_PATH && from !== DELETED_FILE_PATH) {
      status = 'modified';
      diff.status = [1, 2, 1];
    } else {
      status = to === DELETED_FILE_PATH ? 'deleted' : 'new';
      diff.status = status === 'new' ? [0, 2, 0] : [1, 0, 1];
    }
    if (status === 'deleted') {
      diff.path = from;
    }

    const headerEnd = this.end;

    const hasMoreHunks = () => {
      const peek = this.peek();
      return peek && !this.text.startsWith(DIFF_STARTING_LINE, this.end + 1);
    };

    while (hasMoreHunks()) {
      this.nextLine();
    }

    diff.text = this.text.substring(headerEnd + 1, this.end);

    return diff;
  }

  /**
   * Aligns the internal character pointers at the boundaries of
   * the next line.
   *
   * Returns true if successful or false if the end of the git-diff
   * has been reached.
   */
  private nextLine(): boolean {
    this.start = this.end + 1;
    if (this.start >= this.text.length) {
      return false;
    }

    this.end = this.text.indexOf('\n', this.start);
    // if there is no next newline
    if (this.end === -1) {
      this.end = this.text.length;
    }

    // finish if there is nothing to read between cursors
    return this.start !== this.end;
  }

  /**
   * Returns the line content at the current cursor
   */
  private get currentLine() {
    return this.text.substring(this.start, this.end);
  }

  /**
   * Returns the starting character of the next line without
   * advancing the internal state. Returns null if advancing
   * would mean reaching the end of the git-diff.
   */
  private peek(): string | null {
    const p = this.end + 1;
    return p < this.text.length ? this.text[p] ?? null : null;
  }

  private lineStartsWith(str: string) {
    return this.text.startsWith(str, this.start);
  }

  /**
   * Resets the internal parser state so that it can be reused.
   *
   * This is done automatically at the end of each parse run.
   */
  private reset() {
    this.start = 0;
    this.end = -1;
    this.text = '';
  }
}
